import type { CSSProperties } from "react";
import type { TFunction } from "i18next";
import { ActionIcon, Group, Slider } from "@mantine/core";
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconRewindBackward30,
  IconRewindForward30,
  IconVolume,
  IconVolumeOff,
} from "@tabler/icons-react";
import { usePlayer } from "../state/player";
import type { Layout } from "../ui/useLayout";
import type { TapHoldHandlers } from "./useTapHold";

/** The spec's tap distance. */
export const NUDGE_SECONDS = 30;

/**
 * The nudge glyphs spell the number out, so the number cannot be changed on its own.
 *
 * A `NUDGE_SECONDS` this table has no row for stops compiling, which is the prompt to add the
 * matching pair rather than ship a button captioned 30 that seeks 15. Tabler draws the pair
 * for 5, 10, 15, 20, 30, 40, 45, 50 and 60; anything else needs a plain arrow and a label.
 */
const NUDGE_GLYPHS = { 30: [IconRewindBackward30, IconRewindForward30] } as const;
const [NudgeBack, NudgeForward] = NUDGE_GLYPHS[NUDGE_SECONDS];

/**
 * The two hold buttons' gesture is a long press, and without this the browser offers to select
 * text or raises a callout halfway through it. Only back and forward hold; the primary is a
 * plain click.
 */
const HOLD_GESTURE: CSSProperties = {
  touchAction: "none",
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTouchCallout: "none",
};

export interface TransportProps {
  layout: Layout;
  t: TFunction;
  playing: boolean;
  loading: boolean;
  muted: boolean;
  volume: number;
  /** A selected row whose audio the buffer no longer holds: there is nothing to start. */
  expired: boolean;
  back: TapHoldHandlers;
  forward: TapHoldHandlers;
  toggle: () => void;
}

/**
 * A wrapper with nothing to say on a phone -- the same row the two would have had anyway. Off
 * it, it is what makes the transport and the volume the same width: the buttons sit at its ends
 * and the slider runs between them, so the volume bar starts under the back button and stops
 * under the forward one instead of running the card. The one number is how much of the card the
 * transport spans.
 */
export function Transport({
  layout,
  t,
  playing,
  loading,
  muted,
  volume,
  expired,
  back,
  forward,
  toggle,
}: TransportProps) {
  return (
    <Group
      wrap="nowrap"
      w={layout.phone ? "100%" : "72%"}
      gap={layout.phone ? 12 : 14}
      style={{
        flexDirection: layout.phone ? "row" : "column",
        ...(layout.phone && !layout.landscape && { gridColumn: "1 / -1" }),
      }}
    >
      <Group
        gap={layout.phone ? 10 : 14}
        wrap="nowrap"
        // To the ends, so the row has an extent the volume below can be measured against.
        w={layout.phone ? undefined : "100%"}
        justify={layout.phone ? undefined : "space-between"}
        style={layout.phone ? { flex: "none" } : undefined}
      >
        <ActionIcon
          size={layout.phone ? "lg" : "xl"}
          radius="xl"
          style={HOLD_GESTURE}
          aria-label={t("player.backLabel", { seconds: NUDGE_SECONDS })}
          title={t("player.backHint", { seconds: NUDGE_SECONDS })}
          {...back}
        >
          {/* The glyph spells the tap distance out; the double-triangle transport arrows
              this replaced read as a track step, which is the hold. See NUDGE_GLYPHS. */}
          <NudgeBack size={layout.phone ? 22 : 26} stroke={1.5} />
        </ActionIcon>
        <ActionIcon
          variant="filled"
          color="accent"
          size={layout.phone ? 56 : 72}
          radius="xl"
          aria-label={playing ? t("player.pause") : t("player.play")}
          aria-busy={loading}
          // Buffering dims the control without taking it away. Deliberately not Mantine's
          // `loading` prop, which disables the button -- pause has to stay reachable while
          // the stream fills.
          opacity={loading ? 0.6 : undefined}
          // A selected row whose audio has expired has nothing to start. Left enabled, the
          // toggle fell through to the live stream, so pressing play on a 2019 track played
          // whatever is on air right now. Back and forward stay live: stepping to a
          // neighbour is the way off this row.
          disabled={expired}
          title={expired ? t("player.expiredHint") : undefined}
          onClick={toggle}
        >
          {playing ? (
            <IconPlayerPauseFilled size={layout.phone ? 24 : 30} />
          ) : (
            <IconPlayerPlayFilled size={layout.phone ? 24 : 30} />
          )}
        </ActionIcon>
        <ActionIcon
          size={layout.phone ? "lg" : "xl"}
          radius="xl"
          style={HOLD_GESTURE}
          aria-label={t("player.forwardLabel", { seconds: NUDGE_SECONDS })}
          title={t("player.forwardHint", { seconds: NUDGE_SECONDS })}
          {...forward}
        >
          <NudgeForward size={layout.phone ? 22 : 26} stroke={1.5} />
        </ActionIcon>
      </Group>

      <Group
        gap={8}
        justify={layout.phone ? "flex-end" : "center"}
        wrap="nowrap"
        w={layout.phone ? "auto" : "100%"}
        style={layout.phone ? { flex: 1, minWidth: 0 } : undefined}
      >
        <ActionIcon
          aria-label={muted ? t("player.unmute") : t("player.mute")}
          // A toggle, and now it says so. Without this the only report of mute is a glyph.
          aria-pressed={muted}
          onClick={() => usePlayer.getState().setMuted(!muted)}
        >
          {muted || volume === 0 ? (
            <IconVolumeOff size={18} stroke={1.5} />
          ) : (
            <IconVolume size={18} stroke={1.5} />
          )}
        </ActionIcon>
        {/* `thumbLabel`, not `aria-label`: the thumb is the element carrying `role="slider"`,
            so a label on the root would name a wrapper nothing announces. */}
        <Slider
          // The slider takes the row: a fixed 110px bar left the volume looking like a
          // detail of the card rather than one of its four controls.
          w="auto"
          style={{ flex: 1, minWidth: 0 }}
          min={0}
          max={1}
          step={0.01}
          value={muted ? 0 : volume}
          onChange={(value) => usePlayer.getState().setVolume(value)}
          label={null}
          thumbLabel={t("player.volume")}
        />
      </Group>
    </Group>
  );
}
